// A simple cache object to store completed fetch results (parsed data)
const dataCache: { [url: string]: unknown | undefined } = {}

// A cache to store ongoing fetch promises to prevent duplicate requests
const fetchPromises: { [url: string]: Promise<unknown> | undefined } = {}

/**
 * Fetches a resource, ensuring only one fetch is in progress for a given URL at any time.
 * Subsequent calls for the same URL while a fetch is ongoing will await the existing fetch.
 * Caches the parsed response data to avoid re-reading the response stream.
 *
 * @param url The URL to fetch.
 * @param options Optional fetch options.
 * @returns A Promise that resolves with the parsed fetch response data.
 */
export const fetchOnce = async (
  url: string,
  options?: RequestInit
): Promise<unknown> => {
  // Promise now resolves with parsed data
  // Check if data is already in cache
  if (fetchPromises[url]) {
    console.debug(`[fetch] Returning cached data for ${url}`)
    return fetchPromises[url]
  }

  // Check if a fetch for this URL is already in progress
  if (fetchPromises[url]) {
    console.debug(`[fetch] Awaiting existing fetch promise for ${url}`)
    return fetchPromises[url]
  }

  console.debug(`[fetch] Initiating new fetch for ${url}`)
  // If not, start a new fetch and store the promise
  const fetchPromise = (async () => {
    const response = await fetch(url, options)

    if (!response.ok) {
      // Remove the promise from ongoing fetches on failure
      delete fetchPromises[url]
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    // Parse the response body (assuming JSON, adjust as needed based on expected response type)
    // This is where the response stream is consumed once.
    const data = await response.json()

    // Store the parsed data in the cache
    dataCache[url] = data

    // Remove the promise from ongoing fetches on success
    delete fetchPromises[url]

    return data
  })()

  // Store the promise in the ongoing fetches cache
  fetchPromises[url] = fetchPromise

  try {
    // Await the fetch and data parsing promise
    return await fetchPromise
  } catch (error) {
    console.error(`[fetch] Fetch failed for ${url}:`, error)
    // The promise was already removed from fetchPromises in the async IIFE
    throw error // Re-throw the error
  }
}
